import { useEffect, useRef } from 'react'

/**
 * Three unbroken minutes of an empty room ends a hidden observation.
 *
 * A Ghost holds the LiveKit room — and therefore its auto-egress recording —
 * open for as long as it stays connected, and no one can see it leave. So once a
 * ghost has actually watched the class populate, a room that then empties and
 * STAYS empty must not be kept alive by the observer alone: the ghost
 * disconnects onto the normal "left" screen (rejoinable, in case the class comes
 * back after a blip).
 *
 * Two guards make this safe:
 *  - Only AFTER the room was non-empty. A ghost who arrives before the first
 *    student is legitimately waiting, and must never be evicted for it.
 *  - The empty-clock resets the instant anyone reappears. A reconnect that
 *    momentarily drops the visible count to zero, or a brief lull between one
 *    person leaving and the next arriving, never trips it — only a genuine,
 *    continuous three-minute emptiness does.
 */
const EMPTY_GRACE_MS = 3 * 60 * 1000

export function useGhostAutoLeave(
  active: boolean,
  visibleRemoteCount: number,
  onEmpty: () => void,
): void {
  // Latch: the room has to have been non-empty at least once before an empty
  // room means "the class left" rather than "the class has not started".
  const seenSomeoneRef = useRef(false)
  // Kept in a ref so the effect does not re-run (and reset the timer) just
  // because the caller passed a fresh onEmpty closure this render.
  const onEmptyRef = useRef(onEmpty)
  onEmptyRef.current = onEmpty

  useEffect(() => {
    if (!active) return
    if (visibleRemoteCount > 0) {
      seenSomeoneRef.current = true
      // Someone is present: schedule nothing. The cleanup from the previous
      // (empty) run has already cleared any pending timer.
      return
    }
    // Empty but never populated yet — still arriving. Do not start the clock.
    if (!seenSomeoneRef.current) return

    const timer = window.setTimeout(() => onEmptyRef.current(), EMPTY_GRACE_MS)
    return () => window.clearTimeout(timer)
  }, [active, visibleRemoteCount])
}
